import React, { useMemo, useState } from 'react';
import { Modal, Box, Typography, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonLoading from '../../../form-components/CommonLoading';
import CommonSelect from '../../../form-components/CommonSelect';
import { getProductChangeHistory } from '../../../api/product-change-history';

const ModalContainer = styled(Box)({
  width: 'min(1120px, 94vw)',
  maxHeight: '84vh',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  background: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
  flexShrink: 0,
});

const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { color: '#fff', fontSize: '24px' },
});

const ModalContent = styled(Box)({
  padding: '24px 32px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  overflowY: 'auto',
});

const ProductName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  padding: '10px 14px',
  background: '#F7F8F9',
  borderRadius: '6px',
});

const FilterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const FilterLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#5C6470',
  whiteSpace: 'nowrap',
});

const TotalCount = styled(Typography)({
  fontSize: '13px',
  color: '#8C8C8C',
  marginLeft: 'auto',
});

// 모달을 키워도 좁은 화면에서는 표가 넘친다. 그때만 가로 스크롤.
const TableWrap = styled(Box)({
  overflowX: 'auto',
});

const HeaderCell = styled(TableCell)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#5C6470',
  background: '#F7F8F9',
  borderBottom: '1px solid #E0E4EA',
  whiteSpace: 'nowrap',
});

const BodyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#262626',
  borderBottom: '1px solid #EEF1F5',
  // 값이 길어도 두 줄로 흐르지 않게 한 줄 + 말줄임. 전체 값은 title 로 본다.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0,
});

const EmptyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#8C8C8C',
  textAlign: 'center',
  padding: '48px 0',
  borderBottom: 'none',
});

const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 32px 28px',
  flexShrink: 0,
});

// CommonSelect 의 emptyLabel 옵션이 쓰는 빈 값 = 필터 없음.
const ALL = '';

const toDateTime = value => (value ? dayjs(value).format('YYYY.MM.DD HH:mm') : '-');

/** 값 하나가 비어 있어도 화살표는 유지해야 "무엇에서 무엇으로" 가 읽힌다. */
const describe = row => `${row?.beforeValue ?? '-'} → ${row?.afterValue ?? '-'}`;

/**
 * 수강권·이용권 상품 1건의 변경 로그.
 *
 * <p>행 하나가 바뀐 항목 하나라, 같은 저장(eventKey)이 여러 줄로 나뉜다. 그래도 줄마다 실행일을 찍는다.
 *
 * <p>과거분(source=MIRROR_BACKFILL)은 기존 트리거 미러에서 채운 것이라 처리지점이 없고
 * 이용지점·자동차감 같은 항목도 없다.
 */
export default function ProductChangeHistoryModal({ open, onClose, productType, productId, productName, token }) {
  const [fieldFilter, setFieldFilter] = useState(ALL);

  const { data, isLoading } = useQuery({
    queryKey: ['product-change-history', productType, productId],
    queryFn: () => getProductChangeHistory(productType, productId, token),
    enabled: !!open && !!productId && !!token,
    // 방금 저장한 변경이 바로 보여야 한다. 전역 staleTime(30초) 을 이 쿼리만 끈다.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const fieldOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach(row => {
      if (!seen.has(row.fieldKey)) seen.set(row.fieldKey, row.fieldLabel || row.fieldKey);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);

  const visibleRows = useMemo(
    () => (fieldFilter === ALL ? rows : rows.filter(row => row.fieldKey === fieldFilter)),
    [rows, fieldFilter]
  );

  return (
    <Modal open={!!open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>상품 변경 로그</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <ProductName>{productName || '-'}</ProductName>

          <FilterBar>
            <FilterLabel>변경 항목</FilterLabel>
            <CommonSelect
              value={fieldFilter}
              onChange={event => setFieldFilter(event.target.value)}
              emptyLabel="전체"
              options={fieldOptions}
              width={200}
            />
            <TotalCount>총 {visibleRows.length.toLocaleString()}건</TotalCount>
          </FilterBar>

          {isLoading ? (
            <CommonLoading minHeight={220} />
          ) : (
            <TableWrap>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: '880px' }}>
                <colgroup>
                  <col style={{ width: '140px' }} />
                  {/* 변경 항목 — 분류값이라 잘리면 뜻을 잃는다. 최장 라벨이 한 줄로 들어갈 폭 */}
                  <col style={{ width: '170px' }} />
                  {/* 변경 내용 — 지점 목록처럼 길어질 수 있다. 넘치면 말줄임(마우스 오버로 전체) */}
                  <col />
                  <col style={{ width: '110px' }} />
                  {/* 지점명이 긴 고객사가 있다(예: 모던필라테스 방배랩점) */}
                  <col style={{ width: '170px' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <HeaderCell align="center">실행일</HeaderCell>
                    <HeaderCell align="center">변경 항목</HeaderCell>
                    <HeaderCell align="center">변경 내용</HeaderCell>
                    <HeaderCell align="center">처리자</HeaderCell>
                    <HeaderCell align="center">처리지점</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <EmptyCell colSpan={5}>변경 로그가 없습니다.</EmptyCell>
                    </TableRow>
                  ) : (
                    visibleRows.map(row => (
                      <TableRow key={row.id}>
                        {/* 한 번의 저장이 여러 줄로 나뉘어도 줄마다 시각을 찍는다.
                            첫 줄에만 찍으면 아래 줄이 언제 일인지 읽히지 않는다. */}
                        <BodyCell align="center" title={toDateTime(row.changedAt)} sx={{ color: '#5C6470' }}>
                          {toDateTime(row.changedAt)}
                        </BodyCell>
                        <BodyCell
                          align="center"
                          title={row.fieldLabel || row.fieldKey}
                          sx={{ whiteSpace: 'nowrap', textOverflow: 'clip' }}>
                          {row.fieldLabel || row.fieldKey}
                        </BodyCell>
                        <BodyCell align="center" title={describe(row)}>
                          {describe(row)}
                        </BodyCell>
                        <BodyCell align="center" title={row.managerName || undefined}>
                          {row.managerName || '-'}
                        </BodyCell>
                        <BodyCell align="center" title={row.processedBranchName || undefined}>
                          {row.processedBranchName || '-'}
                        </BodyCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableWrap>
          )}
        </ModalContent>

        <ButtonWrapper>
          <CommonModalButton variant="cancel" onClick={onClose}>
            닫기
          </CommonModalButton>
        </ButtonWrapper>
      </ModalContainer>
    </Modal>
  );
}
